import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import {
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import {
  availabilityLocalTimestamp,
  getApiErrorMessage,
  type IWorkAvailabilityWindow,
} from '@moko/utils';
import { notify } from '../Notify';
import useFetch from '../hooks/useFetch';
import { FormActions } from '../FormActions';
import { useFormLayoutStyles } from '../hooks/useFormLayoutStyles';
import type { IWorkAvailabilityServices } from './types';
import { WorkAvailabilityForm } from './WorkAvailabilityForm/WorkAvailabilityForm';
import {
  emptyWorkAvailabilityForm,
  formFromWindow,
  toWorkAvailabilityRange,
  type WorkAvailabilityFormValues,
} from './WorkAvailabilityForm/workAvailabilityFormValues';

export type WorkAvailabilityEditProps = {
  workId: number;
  windowId: number;
  services: IWorkAvailabilityServices;
  submitLabel: string;
  onClose: () => void;
  onSaved: () => void;
};

const WorkAvailabilityEdit = ({
  workId,
  windowId,
  services,
  submitLabel,
  onClose,
  onSaved,
}: WorkAvailabilityEditProps) => {
  const { t } = useTranslation();
  const styles = useFormLayoutStyles();
  const form = useForm<WorkAvailabilityFormValues>({
    defaultValues: emptyWorkAvailabilityForm(),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset, formState } = form;
  const submitting = formState.isSubmitting;

  const { data, loading, error } = useFetch<IWorkAvailabilityWindow>({
    queryKey: [windowId],
    queryFn: (abortController) =>
      services.getWorkAvailabilityWindow(abortController, windowId),
  });

  useEffect(() => {
    if (data) reset(formFromWindow(data));
  }, [data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    const range = toWorkAvailabilityRange(values);
    if (!range) return;
    try {
      await services.updateWorkAvailabilityWindow(
        new AbortController(),
        windowId,
        {
          workId,
          startAt: availabilityLocalTimestamp(range.startAt),
          endAt: availabilityLocalTimestamp(range.endAt),
          timezoneId: values.timezoneId,
          priority: values.priority,
          sourceControlled: values.sourceControlled.trim() || undefined,
          status: values.status,
          isAllDay: values.isAllDay,
          duration: values.duration,
        }
      );
      onSaved();
    } catch (err: unknown) {
      notify.error(
        getApiErrorMessage(
          err,
          t('failedToSaveWindow', 'Failed to save availability window.')
        )
      );
    }
  });

  if (loading) {
    return <Spinner label={t('loading', 'Loading...')} />;
  }

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {getApiErrorMessage(
            error,
            t('failedToLoadWindow', 'Failed to load window.')
          )}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <form className={styles.form} onSubmit={onSubmit}>
        <WorkAvailabilityForm disabled={submitting} />
        <FormActions
          disabled={submitting}
          submitting={submitting}
          submitLabel={submitLabel}
          onClose={onClose}
        />
      </form>
    </FormProvider>
  );
};

export default WorkAvailabilityEdit;
